import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  WxbAlert,
  WxbBadge,
  WxbButton,
  WxbDataTable,
  WxbInput,
  WxbModal,
  WxbPageHeader,
  WxbPageSection,
  WxbPageShell,
  WxbSearchInput,
  WxbSpinner,
  WxbTag,
} from '../../components/wxb-ui';
import type { WxbTagColor } from '../../components/wxb-ui/Tag/Tag';
import { wxbToast } from '../../components/wxb-ui/Toast/Toast';
import { useAuth } from '../../contexts/AuthContext';
import {
  platformIdentityApi,
  type CreatePlatformIdentityPayload,
  type PlatformIdentity,
  type PlatformIdentityStatus,
} from '../../services/platformIdentityApi';
import './PlatformIdentityManagementPage.css';

const STATUS_META: Record<PlatformIdentityStatus, { label: string; color: WxbTagColor }> = {
  ACTIVE: { label: '启用', color: 'green' },
  INACTIVE: { label: '停用', color: 'amber' },
  LOCKED: { label: '锁定', color: 'red' },
  RETIRED: { label: '已退役', color: 'neutral' },
};

interface CreateIdentityForm extends CreatePlatformIdentityPayload {
  confirmPassword: string;
}

const EMPTY_CREATE_FORM: CreateIdentityForm = {
  username: '',
  displayName: '',
  email: '',
  temporaryPassword: '',
  confirmPassword: '',
};

const formatTimestamp = (value: string | null): string => {
  if (!value) return '从未登录';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(parsed);
};

const mapCreateError = (error: unknown): string => {
  const response = (error as {
    response?: { data?: { code?: string; error?: string } };
  })?.response?.data;
  switch (response?.code) {
    case 'USERNAME_EXISTS':
      return '该用户名已存在';
    case 'EMAIL_EXISTS':
      return '该邮箱已被其他平台用户使用';
    case 'BAD_REQUEST':
      return response.error || '账号信息不符合要求';
    default:
      return response?.error || '新建用户失败';
  }
};

const PlatformIdentityManagementPage: React.FC = () => {
  const { hasPlatformPermission } = useAuth();
  const canCreate = hasPlatformPermission('PLATFORM_IDENTITY_CREATE');
  const [identities, setIdentities] = useState<PlatformIdentity[]>([]);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateIdentityForm>(EMPTY_CREATE_FORM);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const loadIdentities = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      setIdentities(await platformIdentityApi.listIdentities(appliedSearch || undefined));
    } catch {
      setLoadError(true);
      wxbToast.error('加载平台用户失败');
    } finally {
      setLoading(false);
    }
  }, [appliedSearch]);

  useEffect(() => {
    void loadIdentities();
  }, [loadIdentities]);

  const openCreate = () => {
    setCreateForm(EMPTY_CREATE_FORM);
    setCreateError(null);
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    setCreateError(null);
    if (!createForm.username.trim() || !createForm.displayName.trim()) {
      setCreateError('请填写用户名和显示名称');
      return;
    }
    if (createForm.temporaryPassword.length < 8) {
      setCreateError('临时密码至少需要 8 个字符');
      return;
    }
    if (createForm.temporaryPassword !== createForm.confirmPassword) {
      setCreateError('两次输入的临时密码不一致');
      return;
    }
    setCreating(true);
    try {
      await platformIdentityApi.createIdentity({
        username: createForm.username.trim(),
        displayName: createForm.displayName.trim(),
        email: createForm.email?.trim() || null,
        temporaryPassword: createForm.temporaryPassword,
      });
      wxbToast.success('平台用户已创建；当前尚未获得任何 Site 权限');
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE_FORM);
      await loadIdentities();
    } catch (error) {
      setCreateError(mapCreateError(error));
    } finally {
      setCreating(false);
    }
  };

  const columns = useMemo(() => [
    {
      title: '用户',
      key: 'identity',
      render: (_: unknown, record: PlatformIdentity) => (
        <div className="gv-identity-name-cell">
          <span className="gv-identity-display-name">{record.displayName}</span>
          <span className="gv-identity-username">@{record.username}</span>
        </div>
      ),
    },
    {
      title: '登录方式',
      key: 'authentication',
      render: (_: unknown, record: PlatformIdentity) => (
        <div className="gv-identity-secondary">
          <span>{record.authProvider === 'LOCAL' ? '本地密码' : record.authProvider}</span>
          <span>{record.email ?? '未设置邮箱'}</span>
        </div>
      ),
    },
    {
      title: '账号状态',
      key: 'status',
      render: (_: unknown, record: PlatformIdentity) => (
        <div className="gv-identity-tag-list">
          <WxbTag color={STATUS_META[record.userStatus].color}>
            {STATUS_META[record.userStatus].label}
          </WxbTag>
          {record.mustChangePassword ? <WxbTag color="amber">首次登录需改密</WxbTag> : null}
        </div>
      ),
    },
    {
      title: 'Site 成员关系',
      key: 'memberships',
      render: (_: unknown, record: PlatformIdentity) => (
        record.siteMembershipCount === 0 ? (
          <WxbTag color="neutral">尚未加入 Site</WxbTag>
        ) : (
          <div className="gv-identity-secondary">
            <span>{record.activeSiteMembershipCount} 个有效 Site</span>
            <span>共 {record.siteMembershipCount} 条成员关系</span>
          </div>
        )
      ),
    },
    {
      title: '最近登录',
      key: 'lastLoginAt',
      render: (_: unknown, record: PlatformIdentity) => (
        <span className="gv-identity-secondary-text">
          {formatTimestamp(record.lastLoginAt)}
        </span>
      ),
    },
  ], []);

  return (
    <WxbPageShell size="wide">
      <WxbPageHeader
        eyebrow="平台控制面"
        title="平台用户"
        description="创建和查看全局登录身份。平台账号与 Site 成员关系分离，创建账号不会自动获得任何 Site 数据访问权。"
        meta={!loading && !loadError ? (
          <WxbBadge variant="bar" status="info" label={`显示 ${identities.length} 个用户`} />
        ) : undefined}
        actions={canCreate ? (
          <WxbButton variant="primary" onClick={openCreate}>
            新建用户
          </WxbButton>
        ) : undefined}
      />

      <WxbAlert title="账号创建不等于 Site 授权">
        新用户创建后只能登录并完成首次改密。需要由目标 Site 管理员在“用户授权”中按准确用户名添加成员，再授予本站角色。
      </WxbAlert>

      <WxbPageSection
        title="用户目录"
        description="仅平台身份管理员可查看本目录；Site 管理员不能枚举全局账号。"
        variant="framed"
        actions={(
          <WxbSearchInput
            value={search}
            placeholder="搜索用户名、显示名称或邮箱"
            aria-label="搜索平台用户"
            onChange={setSearch}
            onSearch={(value) => setAppliedSearch(value.trim())}
          />
        )}
      >
        {loading ? (
          <div className="gv-identity-loading">
            <WxbSpinner tip="加载平台用户中…" />
          </div>
        ) : (
          <WxbDataTable<PlatformIdentity>
            rowKey="id"
            columns={columns}
            dataSource={identities}
            pagination={false}
            errorState={loadError ? {
              title: '加载失败',
              description: '无法加载平台用户目录',
              action: (
                <WxbButton variant="secondary" onClick={() => void loadIdentities()}>
                  重试
                </WxbButton>
              ),
            } : undefined}
            emptyState={{
              description: appliedSearch ? '没有匹配的平台用户' : '暂无平台用户',
            }}
          />
        )}
      </WxbPageSection>

      <WxbModal
        open={createOpen}
        title="新建平台用户"
        width={560}
        okText="创建用户"
        confirmLoading={creating}
        destroyOnClose
        onCancel={() => setCreateOpen(false)}
        onOk={submitCreate}
      >
        <div className="gv-identity-form">
          <WxbAlert title="新账号默认零 Site 权限">
            账号创建后不会自动加入当前或其他 Site，也不会自动获得任何角色。
          </WxbAlert>
          <WxbInput
            label="用户名"
            aria-label="用户名"
            value={createForm.username}
            autoComplete="off"
            maxLength={100}
            helpText="3–100 个字符，仅支持字母、数字、点、下划线和连字符"
            onChange={(event) => setCreateForm((current) => ({
              ...current,
              username: event.target.value,
            }))}
          />
          <WxbInput
            label="显示名称"
            aria-label="显示名称"
            value={createForm.displayName}
            autoComplete="off"
            maxLength={200}
            onChange={(event) => setCreateForm((current) => ({
              ...current,
              displayName: event.target.value,
            }))}
          />
          <WxbInput
            label="邮箱（可选）"
            aria-label="邮箱（可选）"
            type="email"
            value={createForm.email ?? ''}
            autoComplete="off"
            maxLength={255}
            onChange={(event) => setCreateForm((current) => ({
              ...current,
              email: event.target.value,
            }))}
          />
          <div className="gv-identity-password-row">
            <WxbInput
              label="临时密码"
              aria-label="临时密码"
              type="password"
              value={createForm.temporaryPassword}
              autoComplete="new-password"
              helpText="至少 8 个字符；用户首次登录后必须修改"
              onChange={(event) => setCreateForm((current) => ({
                ...current,
                temporaryPassword: event.target.value,
              }))}
            />
            <WxbInput
              label="确认临时密码"
              aria-label="确认临时密码"
              type="password"
              value={createForm.confirmPassword}
              autoComplete="new-password"
              onChange={(event) => setCreateForm((current) => ({
                ...current,
                confirmPassword: event.target.value,
              }))}
            />
          </div>
          {createError ? (
            <div className="gv-identity-form-error" role="alert">{createError}</div>
          ) : null}
        </div>
      </WxbModal>
    </WxbPageShell>
  );
};

export default PlatformIdentityManagementPage;
